import { type Member } from '@/features/iam/members/Member'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { resolveOnAbort } from '@/utils/abort-util'
import { action, computed, observable } from 'mobx'
import { Subject } from 'rxjs'
import { distinctUntilChanged } from 'rxjs/operators'
import { toStream } from '@/utils/mobx-util'
import { shareLatest, whileActive } from '@/utils/rx-util'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type WorkspaceSelected } from './DraftPersister'
import { type DraftStateSlice, type PrepareContext } from './DraftJobViewModel'
import { type StepFactory, type StepViewModel } from './OutlineViewModel'
import type {
  ListWorkspaceMembershipsFn,
  GetCurrentMemberFn,
} from '@/features/iam'
import { type PublishedLegendStore } from '@/features/jobs/legends/PublishedLegendStore'
import { Id } from '@/infra/id'
import { type OwnerScopedInput } from './PrefilledDraftInput'
import type { GetDraftingMembersFn } from './drafting-members'

const TITLE = 'Where are you submitting work?'

/**
 * View Model for selecting a Workspace for Job Creation.
 */
export class WorkspaceSelectorViewModel
  implements ActivationLifecycle, DraftStateSlice
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Whether `prepare` has completed since activation.
   */
  @observable private accessor _isReady = false

  private readonly _isReady$ = toStream(() => this._isReady, true)

  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Used to emit changes so we know when to trigger a save.
   *
   * @private
   */
  private readonly _changesSubject = new Subject<WorkspaceSelected>()

  /**
   * Emits a value when the selected workspace was changed by the user.
   */
  readonly changes$ = this._changesSubject.asObservable()

  /**
   * Selected workspace member.
   */
  @observable accessor selectedWorkspaceMember: Member | null = null

  /**
   * Emits the selected Workspace Member (null included) on subscribe,
   * but only after `prepare` has been run.
   */
  readonly selectedWorkspaceMember$ = toStream(
    () => this.selectedWorkspaceMember,
    true,
  ).pipe(
    distinctUntilChanged(),
    shareLatest(),
    // Don't start emitting until the slice is ready.
    whileActive(this._isReady$),
  )

  /**
   * Scoped to owner inputs.
   */
  @observable accessor ownerScopedInput: OwnerScopedInput | undefined

  /**
   * Constructor.
   */
  constructor(
    createStep: StepFactory,
    private readonly _getDraftingMembers: GetDraftingMembersFn,
    private readonly _listWorkspaceMemberships: ListWorkspaceMembershipsFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _publishedLegendStore: PublishedLegendStore,
    private readonly _isDisabled: () => boolean,
  ) {
    this._lifecycle.manage(this.selectedWorkspaceMember$)

    this.step = createStep({
      stepId: 'workspace',
      isCompleted: () => this.selectedWorkspace !== null,
      outlineItemDisplay: () => {
        if (!this.selectedWorkspace) {
          return {
            title: TITLE,
          }
        }
        return {
          title: TITLE,
          value: this.selectedWorkspace.name,
          visual: {
            type: 'Image',
            url: this.selectedWorkspace.branding.logos.icon,
          },
        }
      },
    })
  }

  /**
   * This is the only step accessor.
   * Relevant when we have more than 1 Workspace.
   */
  @computed
  get stepAccessors() {
    // Only relevant if there is more than one Workspace to select from.
    return this.availableWorkspaces.length > 1 ? [this] : []
  }

  /**
   * Getter for the selected workspace.
   */
  @computed
  get selectedWorkspace() {
    return this.selectedWorkspaceMember?.workspace ?? null
  }

  /**
   * Whether this control is readonly.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * The available workspaces for creating jobs.
   */
  @computed
  get availableWorkspaces() {
    const workspaces = this._getDraftingMembers().map((m) => m.workspace)

    if (!this.ownerScopedInput) {
      return workspaces
    }

    return workspaces.filter((w) =>
      this.ownerScopedInput?.workspaceIds.some((id) => id === Id.assert(w.id)),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  @action.bound
  deactivate() {
    this._isReady = false
    this._lifecycle.deactivate()
  }

  /** @inheritDoc */
  @action.bound
  ready() {
    this._isReady = this._lifecycle.active
  }

  /**
   * Hydrates state from a Job.
   */
  async prepare(context: PrepareContext, signal?: AbortSignal) {
    await resolveOnAbort(this._listWorkspaceMemberships(), signal)
    signal?.throwIfAborted()

    if (context.type === 'EditDraft') {
      this.setWorkspace(context.job.workspace)
      return
    }

    // Must clear or set any owner scoped inputs before further preparation
    this.setOwnerScopeInput(context.prefilledDraft.ownerScopedInput)

    if (context.prefilledDraft.legendId) {
      const latestLegend =
        await this._publishedLegendStore.fetchLatestLegendVersion(
          context.prefilledDraft.legendId,
          signal,
        )
      signal?.throwIfAborted()
      this.selectWorkspace(latestLegend.workspace)
      return
    }

    const workspaceId = context.prefilledDraft.ownerScopedInput?.workspaceIds[0]
    const currentMember = this._getCurrentMember(workspaceId)
    this.selectWorkspace(currentMember.workspace)
  }

  /**
   * Sets whether component should be scoped to a job owner.
   *
   * @param scopedInput
   */
  @action.bound
  setOwnerScopeInput(scopedInput?: OwnerScopedInput) {
    this.ownerScopedInput = scopedInput
  }

  /**
   * Sets the selected workspace.
   *
   * @param workspace
   */
  @action.bound
  setWorkspace(workspace: Workspace | null) {
    const member = this._getDraftingMembers().find(
      (m) => m.workspace === workspace,
    )
    this.selectedWorkspaceMember = member ?? null
  }

  /**
   * Selects the specified workspace and emits a notification.
   *
   * @param workspace
   */
  @action.bound
  selectWorkspace(workspace: Workspace | null | undefined) {
    workspace ??= null
    if (this.selectedWorkspace === workspace) {
      return
    }

    this.setWorkspace(workspace)
    this._changesSubject.next({ workspace, type: 'WorkspaceSelected' })
  }
}

/**
 * Factory for creating a {@link WorkspaceSelectorViewModel}.
 */
export type WorkspaceSelectorViewModelFactory = ReturnType<
  typeof createWorkspaceSelectorViewModelFactory
>

/** @see WorkspaceSelectorViewModelFactory */
export function createWorkspaceSelectorViewModelFactory(
  getDraftingMembers: GetDraftingMembersFn,
  listWorkspaceMemberships: ListWorkspaceMembershipsFn,
  getCurrentMember: GetCurrentMemberFn,
  publishedLegendStore: PublishedLegendStore,
) {
  return (createStep: StepFactory, isDisabled: () => boolean) =>
    new WorkspaceSelectorViewModel(
      createStep,
      getDraftingMembers,
      listWorkspaceMemberships,
      getCurrentMember,
      publishedLegendStore,
      isDisabled,
    )
}
